import { Config } from '../video-data/config';
import type { Position, Anchor, CurvePointDescription, RemoveAnchoredPoints } from './Curve';
import { Curve } from './Curve';

export type SteppedCurveDescription = {
    duration?: number;
    classType: 'SteppedCurve';
    points: CurvePointDescription[];
    removeAnchoredPoints?: RemoveAnchoredPoints;
};

export class SteppedCurve extends Curve {
    add(p: Position, anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR) {
        const ep = this._addEndPoint(p);
        ep.anchor = anchor;
    }

    _getEndPointIndexForPercentage(value: number): number | undefined {
        const endPoints = this.getEndPoints();
        const nEndPoints = endPoints.length;
        if (value <= 0.0) {
            return 0;
        } else if (value >= 1.0) {
            return nEndPoints - 1;
        } else {
            if (value < endPoints[0].x) {
                return 0;
            } else if (value >= endPoints[nEndPoints - 1].x) {
                return nEndPoints - 1;
            } else {
                for (let i = 0; i < nEndPoints - 1; i++) {
                    if (endPoints[i].x <= value && value < endPoints[i + 1].x) {
                        return i;
                    }
                }
            }
        }
    }

    _evalPercentage(value: number) {
        const index = this._getEndPointIndexForPercentage(value);
        if (index === undefined) {
            throw Error(`Invalid index ${index} for value ${value}.`);
        }
        const points = this.getPositions();
        return points[index].y;
    }

    /**
     * Overlapping curves cannot be properly evaluated, so this method will check data validity by ensuring all points'
     * x values are continuously increasing.
     */
    _isDataValid(): boolean {
        // ensure that all points are increasing in x value
        const pointsX = this.points.map((point) => point.x);
        const isIncreasing = pointsX.every((number, i) => i === 0 || pointsX[i - 1] <= number);
        if (!isIncreasing) {
            return false;
        }
        return true;
    }

    override setCurveCoordinates(coordinatesString: string) {
        this.clearPoints();
        const tokens = coordinatesString.slice(2, coordinatesString.length).split(' L ');
        const coordinates: Position[] = [];

        for (let i = 0; i < tokens.length; i++) {
            const stokens = tokens[i].split(' ');

            let added = false;
            if (i < tokens.length - 1) {
                const nstokens = tokens[i + 1].split(' ');
                if (stokens[0] === nstokens[0]) {
                    added = true;
                    coordinates.push({
                        x: parseFloat(nstokens[0]),
                        y: parseFloat(nstokens[1]),
                    });
                    i++;
                }
            }

            if (!added) {
                coordinates.push({
                    x: parseFloat(stokens[0]),
                    y: parseFloat(stokens[1]),
                });
            }
        }

        coordinates.forEach((coordinate) => {
            this.add(coordinate);
        });
    }

    override getCurveCoordinates(precision = 5) {
        let c = '';
        const points = this.getEndPoints();
        const np = points.length;
        for (let i = 0; i < np; i++) {
            if (i !== 0) {
                if (points[i].y !== points[i - 1].y) {
                    c += ` L ${parseFloat(points[i].x.toFixed(precision))}, ${parseFloat(points[i - 1].y.toFixed(precision))}`;
                }
            }
            c += i === 0 ? 'M' : ' L ';
            c += `${parseFloat(points[i].x.toFixed(precision))}, ${parseFloat(points[i].y.toFixed(precision))}`;
        }
        return c;
    }

    override getCurveDescription(precision = 5): SteppedCurveDescription {
        const desc: SteppedCurveDescription = {
            duration: this.duration,
            classType: 'SteppedCurve',
            points: [],
        };

        const endPoints = this.getEndPoints();
        for (let i = 0; i < endPoints.length; i++) {
            desc.points.push({
                anchor: endPoints[i].anchor,
                position: endPoints[i].getPosition(precision),
            });
        }

        return desc;
    }

    override setCurveDescription(data: SteppedCurveDescription) {
        this.clearPoints();

        if (data.hasOwnProperty('duration')) {
            // directly set duration without scaling end points.
            this._duration = data.duration!;
        }

        const pointData = data.points;

        for (let i = 0; i < pointData.length; i++) {
            const anchor = pointData[i].anchor || Config.MIDDLE_ANIMATION_CURVE_ANCHOR;
            const px = pointData[i].position.x;
            const py = pointData[i].position.y;
            const p = { x: px, y: py };
            this.add(p, anchor);
        }

        // try to automatically assign anchors when none are provided
        if (!this.hasBoundaryAnchors()) {
            this.assignBoundaryAnchors();
        }

        if (data.removeAnchoredPoints !== Config.NONE_ANIMATION_CURVE_ANCHOR) {
            this.removePointsWithAnchor(data.removeAnchoredPoints!);
        }
    }

    static fromDescription(description: SteppedCurveDescription) {
        const instance = new SteppedCurve();
        instance.setCurveDescription(description);
        return instance;
    }
}
